import React, { forwardRef } from 'react';
import { mescola } from '../../utils/colori';
import {
    durataMinuti, nomeAppuntamento, orarioDi, prestazioniDi, statoDi, ALTEZZA_ORA,
} from './griglia';

/**
 * BloccoAppuntamento — un appuntamento sulla griglia, uguale nella vista
 * del giorno e in quella della settimana (stile «Console», 6/10/2026).
 *
 * IL COLORE E' QUELLO DELL'AGENDA, nei dati (Alex, 2/10/2026: nessuna
 * famiglia di colori nuova, le categorie usano i colori che agende e stanze
 * hanno gia'). Come nel mockup «Agenda»: lo sfondo e' la tinta chiara del
 * colore e il testo la sua tinta scura, mescolati col bianco e col nero
 * (utils/colori.js) e non con la trasparenza, cosi' non dipendono dalla
 * fascia che c'e' sotto. Un'agenda senza colore resta sui token del tema.
 * I blocchi (pranzo, riunioni) non sono di nessun paziente: restano neutri,
 * a righe.
 *
 * Il pallino davanti al nome dice a che punto e' il paziente e si legge
 * senza aprire niente: verde «uscito» vince sul giallo «arrivato» (il
 * giallo dice «Arrivato», non «in sala d'attesa»: quella ha una definizione
 * sola, services/salaAttesa.js, ed e' il numero accanto ad Agenda).
 *
 * «Sovrapposto» compare quando un altro appuntamento non annullato occupa
 * la stessa fascia nella stessa agenda o nella stessa stanza: lo dice la
 * rotta (x.sovrapposto, agendaController, SI_ACCAVALLA), con la stessa
 * definizione dell'avviso che da' salvando («sovrapposto ad altri gia'
 * presenti», «l'apparecchiatura e' gia' impegnata»). Non blocca niente,
 * come non lo blocca il server: chi guarda deve accorgersene.
 */
const BloccoAppuntamento = forwardRef(({
    x, colore, stile, scelto = false, trascinato = false, className = '', ...gestori
}, ref) => {
    const stato = statoDi(x);
    const altezza = (durataMinuti(x) / 60) * ALTEZZA_ORA;
    /* Sotto i 30 minuti c'e' posto per una riga sola: ora e nome affiancati;
       fino a tre quarti d'ora due righe, ora e nome. */
    const corto = altezza < 38;
    const medio = !corto && altezza < 56;
    const conColore = x.tipo !== 'blocco' && !!colore;
    const classi = ['ag-app', `ag-${x.tipo}`, `ag-stato-${x.stato}`, `ag-app--${stato}`];
    if (corto) classi.push('ag-app--corto');
    if (medio) classi.push('ag-app--medio');
    if (x.sovrapposto) classi.push('ag-app--sovrapposto');
    if (x.dietro) classi.push('ag-app--dietro');
    if (scelto) classi.push('ag-app--scelto');
    if (trascinato) classi.push('ag-trascinato');
    if (className) classi.push(className);
    const cosa = x.tipo === 'blocco' ? '' : prestazioniDi(x);

    return (
        <button
            ref={ref}
            type="button"
            data-appuntamento={x.id}
            className={classi.join(' ')}
            aria-pressed={scelto}
            style={{
                ...stile,
                ...(conColore ? {
                    background: mescola(colore, 0.8),
                    color: mescola(colore, -0.55),
                    '--ag-colore': colore,
                } : {}),
            }}
            {...gestori}
        >
            {/* L'ora e «Sovrapposto» stanno sulla stessa riga: nella mezz'ora, la
                misura piu' comune, una terza riga non c'e' posto e veniva
                tagliata (rifinitura, 14/10/2026). */}
            <span className="ag-app-riga">
                <span className="ag-app-ora rm-numeri">{orarioDi(x)}</span>
                {x.sovrapposto && (
                    <span className="ag-app-sovrapposto rm-tronca"
                          title="Un altro appuntamento occupa la stessa fascia, nella stessa agenda o nella stessa stanza">
                        Sovrapposto
                    </span>
                )}
            </span>
            <span className="ag-app-nome">
                {stato === 'uscito' ? (
                    <i className="ag-stato-pallino ag-sp-verde"
                       title="Uscito: visita pagata, o chiusa senza niente da incassare" />
                ) : stato === 'in_visita' ? (
                    <i className="ag-stato-pallino ag-sp-in-visita" title="In visita" />
                ) : stato === 'arrivato' ? (
                    <i className="ag-stato-pallino ag-sp-giallo" title="Arrivato" />
                ) : null}
                {nomeAppuntamento(x)}
            </span>
            {cosa && <span className="ag-app-cosa">{cosa}</span>}
        </button>
    );
});

BloccoAppuntamento.displayName = 'BloccoAppuntamento';

export default BloccoAppuntamento;
